// Import react stuff
import { useState, useEffect } from 'react';

// Import animation, motion
import { AnimatePresence, motion } from 'framer-motion';
import { fadeInOut, containerVariants, fadeInOutSurvey } from '../../utils/animations';

// Import Redux stuff
import { useSelector, useDispatch } from 'react-redux';
import {
  setCorrectAnswers,
  setCurrentQuestion,
  setCurrentSurvey,
  setShowResult,
  setShowTrivia,
  setShowSurvey,
  setCurrentSurveyQuestion,
  setChosenProducts,
  setChosenAnswer,
  setSelectedAnswers,
  setUserSegment,
  setInitialSurveyCompleted,
  setSurveyPath,
  questionTimeReducer,
  setQuestionTime,
} from '../../store/gameSlice';
import { setShowOptin } from '../../store/userSlice';

// Import components
import Survey from '../QuizSurvey/Survey';
import Trivia from '../QuizTrivia/Trivia';
import Question from '../../components/Question';
import QuizStatus from '../../components/QuizStatus';

// Import helper functions
import { importAll } from '../../utils/helpers';

const Quiz = ({ quizData, data, showSurvey }) => {
  const dispatch = useDispatch();
  const questions = quizData?.questions;

  // Import images
  const quizImages = importAll(
    require.context('../../assets/images/quiz', false, /\.(png|jpe?g|svg|gif)$/)
  );

  const questionTime = 30;

  // Is form submitted
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  // Global states - RTK Toolkit
  const currentQuestion = useSelector((state) => state.games.currentQuestion);
  //const correctAnswers = useSelector((state) => state.games.correctAnswers);
  const showTrivia = useSelector((state) => state.games.showTrivia);
  const currentSurvey = useSelector((state) => state.games.currentSurvey);
  const currentSurveyQuestion = useSelector((state) => state.games.currentSurveyQuestion);
  const initialSurveyCompleted = useSelector((state) => state.games.initialSurveyCompleted);
  const surveyPath = useSelector((state) => state.games.surveyPath);
  const chosenProducts = useSelector((state) => state.games.chosenProducts);
  const questionTimeLeft = useSelector((state) => state.games.time);

  // Score and success rate calculations
  //let correctAnswersNum = correctAnswers.filter((x) => x === true).length;
  let totalQuestionsNum = questions?.length;

  // On answer click
  const clickHandler = (e, is_correct, answerText) => {
    setIsFormSubmitted(true);

    // set time to zero
    dispatch(setQuestionTime(null));

    // Find correct answer(s)
    let correctAnswerIndices = questions[currentQuestion].answerOptions
      .map((answer, index) => (answer.is_correct ? index : -1))
      .filter((index) => index !== -1);

    // find correct answers, clicked answer and list all buttons to clear classes on next step
    let answerCorrectBtns = correctAnswerIndices.map((index) =>
      document.getElementById(`answer_${index}`)
    );
    let answerClickedBtn = document.getElementById(e.currentTarget.id);

    // add correct or wrong class
    answerClickedBtn.classList.add(is_correct ? 'correct' : 'wrong');
    answerCorrectBtns.forEach((btn) => btn.classList.add('correct'));

    // Add true/false to array and send to global state
    dispatch(setCorrectAnswers(is_correct));

    // Add answer text to array and send to global state
    dispatch(setSelectedAnswers(answerText));

    handleCheckForTrivia();
  };

  // Check if trivia is available for the current question
  const handleCheckForTrivia = () => {
    let trivia = questions[currentQuestion].trivias;
    if (trivia && (trivia.trivia_heading || trivia.trivia_text || trivia.trivia_image)) {
      // Show trivia and handle next question after a timeout
      setTimeout(() => {
        dispatch(setShowTrivia(true));
      }, 2000);
    } else {
      // Move to next question automatically after a timeout
      setTimeout(() => {
        handleNextQuestion();
      }, 2000);
    }
  };

  // Handle trivia button click – show mid-quiz survey by path and question index
  const handleTriviaClick = () => {
    dispatch(setShowTrivia(false));

    // Path 1 (owner): survey 2 after Q2 (index 1), survey 3 after Q5 (index 4)
    // Path 2 (non-owner): survey 3 only after Q5 (index 4)
    const shouldShowSurvey2AfterQ2 = surveyPath === 1 && currentQuestion === 1;
    const shouldShowSurvey3AfterQ5 = currentQuestion === 4;

    if (shouldShowSurvey2AfterQ2) {
      dispatch(setCurrentSurvey(1));
      dispatch(setCurrentSurveyQuestion(1)); // survey 1 content uses id: 1
      dispatch(setShowSurvey(true));
    } else if (shouldShowSurvey3AfterQ5) {
      dispatch(setCurrentSurvey(2));
      dispatch(setCurrentSurveyQuestion(2)); // survey 2 content uses id: 2
      dispatch(setShowSurvey(true));
    } else {
      handleNextQuestion();
    }
  };

  const handleNextQuestion = () => {
    setIsFormSubmitted(false);
    handleResetTimer();

    // Remove classes for next questoon
    let answersAllButtons = document.querySelectorAll('.answer-section button');
    answersAllButtons.forEach(function (element) {
      element.classList.remove('correct');
      element.classList.remove('wrong');
    });

    // move to next question
    let nextQuestion = currentQuestion + 1;

    // Move to next question or to result screen
    if (nextQuestion < totalQuestionsNum) {
      dispatch(setCurrentQuestion(nextQuestion));
    } else if (nextQuestion === totalQuestionsNum) {
      //dispatch(scoreReducer(correctAnswersNum));

      const segment = defineUserSegment(chosenProducts);
      dispatch(setUserSegment(segment));

      dispatch(setShowOptin(true));
      dispatch(setShowResult(true));
    }
  };

  // Survey – initial survey (before quiz) or mid-quiz surveys
  const handleSurveyClick = (path, answerTags, answerText) => {
    window.scrollTo(0, 0);

    // Plausible event - track selected survey answer
    if (window.plausible) {
      window.plausible('poll', { props: { answer: answerText } });
    }

    dispatch(setChosenProducts([answerTags]));
    dispatch(setChosenAnswer({ step: currentSurvey + 1, answer: answerText }));

    // Initial survey (before first quiz question): store path and close survey
    if (currentSurvey === 0 && currentSurveyQuestion === 0) {
      dispatch(setSurveyPath(path));
      dispatch(setInitialSurveyCompleted(true));
      dispatch(setQuestionTime(questionTime));
      setTimeout(() => {
        dispatch(setShowSurvey(false));
      }, 1000);
      return;
    }

    const nextQuestion = path;
    const currentSurveyContent = quizData.survey[currentSurvey].content;

    if (nextQuestion < currentSurveyContent.length) {
      setTimeout(() => {
        dispatch(setCurrentSurveyQuestion(nextQuestion));
      }, 1000);
    } else {
      setTimeout(() => {
        dispatch(setCurrentSurveyQuestion(0));
        dispatch(setShowSurvey(false));
        handleNextQuestion();
      }, 1000);
    }
  };

  // Find object of current survey
  const filterCurrentSurveyQuestion = quizData.survey[currentSurvey].content.find(
    (obj) => obj.id === currentSurveyQuestion
  );

  // Set user segment based on survey's selected tags
  const defineUserSegment = () => {
    const owner = chosenProducts.includes('s1a1');
    const club = chosenProducts.includes('s2a1');

    if (owner && club) {
      return 'LASTNIK + POOBLAŠČENI';
    } else if (owner && !club) {
      return 'LASTNIK - NEPOOBLAŠČENI';
    } else {
      return 'NELASTNIK';
    }
  };

  // Question timer
  const handleResetTimer = () => {
    // reset question time to initial value
    clearInterval(timer);
    dispatch(setQuestionTime(questionTime));
  };

  // time per question
  let timer;

  // Show first survey before any quiz question when entering the quiz
  useEffect(() => {
    if (!initialSurveyCompleted) {
      dispatch(setShowSurvey(true));
      dispatch(setCurrentSurvey(0));
      dispatch(setCurrentSurveyQuestion(0));
    }
  }, [dispatch, initialSurveyCompleted]);

  useEffect(() => {
    const timer = setInterval(() => {
      dispatch(questionTimeReducer());
    }, 1000);
    return () => clearInterval(timer);
  }, [dispatch, currentQuestion]);

  // When time's up and form is not submitted
  useEffect(() => {
    if (questionTimeLeft === 0 && !isFormSubmitted && !showSurvey) {
      handleCheckForTrivia();
    }
  }, [questionTimeLeft]);

  // Scroll to top when switching between question, trivia, and survey
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      const mainContainer = document.querySelector('.main-container');
      if (mainContainer && mainContainer.scrollHeight > mainContainer.clientHeight) {
        mainContainer.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    };
    const timeoutId = setTimeout(scrollToTop, 0);
    return () => clearTimeout(timeoutId);
  }, [showTrivia, showSurvey, currentQuestion, currentSurveyQuestion]);

  return (
    <AnimatePresence mode="wait">
      {showTrivia ? (
        <motion.div
          variants={fadeInOut}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="quiz wrapper inner-wrapper"
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="trivia"
          >
            <Trivia
              data={questions[currentQuestion]}
              handleTriviaClick={handleTriviaClick}
              quizImages={quizImages}
              text={data}
            />
          </motion.div>
        </motion.div>
      ) : showSurvey ? (
        <motion.div variants={fadeInOutSurvey} initial="hidden" animate="visible" exit="exit">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="survey"
          >
            <Survey
              filterCurrentSurvey={filterCurrentSurveyQuestion}
              handleSurveyClick={handleSurveyClick}
              data={data}
            />
          </motion.div>
        </motion.div>
      ) : (
        <motion.div
          variants={fadeInOut}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="quiz wrapper inner-wrapper"
        >
          <QuizStatus
            totalQuestionsNum={totalQuestionsNum}
            currentQuestion={currentQuestion}
            isFormSubmitted={isFormSubmitted}
            questionTimeLeft={questionTimeLeft}
            questionTime={questionTime}
            data={data}
          />
          <Question
            data={questions[currentQuestion]}
            clickHandler={clickHandler}
            isFormSubmitted={isFormSubmitted}
            quizImages={quizImages}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Quiz;
